import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Col, Input, Row } from 'antd'

import HowToFindItemNumber from '@/components/HowToFindItemNumber'
import { AlertIcon } from '@/components/Icons'
import ImageWithFallBack from '@/components/ImageWithFallback'
import { useDebounce } from '@/hooks'
import { ASYNC_STATUS, isValidMaterial } from '@/libs/constants'
import { formatMaterialID } from '@/libs/utils/material-number'
import greenCheckIconImage from '@/assets/img/green-check-icon.webp'
import moreInfoIconImage from '@/assets/img/more-info-icon.webp'

import DisisHomeButton from './DisisHomeButton'
import styles from './SparePartsFinderInput.module.scss'
import SparePartsProductInfo from './SparePartsProductInfo'

const SparePartsFinderInput = () => {
  const { t } = useTranslation()
  const [itemNumber, setItemNumber] = useState('')
  const [isItemNumberInfoShown, setIsItemNumberInfoShown] = useState(false)
  const [iconState, setIconState] = useState(ASYNC_STATUS.IDLE)

  const debouncedItemNumber = useDebounce(itemNumber, 500)

  const handleSetIconState = useCallback((state) => {
    setIconState(state)
  }, [])

  const handleInputValue = (string_) => {
    if (string_.length <= 8 && /^\d{8}$/.test(string_)) {
      const formattedString = formatMaterialID(string_)
      return formattedString
    }
    return string_
  }

  const handleChange = (e) => {
    const value = e.target.value.trim()
    const re = /^[\d, .-]+$/
    if (value !== '' && re.test(value)) {
      const formattedValue = handleInputValue(value)
      setItemNumber(formattedValue)
    } else {
      setItemNumber(value.replaceAll(/[^\d, .-]/g, ''))
    }
    setIconState(ASYNC_STATUS.IDLE)
  }

  const toggleInfoShow = () => {
    setIsItemNumberInfoShown(!isItemNumberInfoShown)
  }

  const renderValidationIcon = () => {
    if (iconState === ASYNC_STATUS.ERROR && isValidMaterial(itemNumber)) {
      return (
        <div className={styles.error}>
          <AlertIcon />
        </div>
      )
    }

    if (iconState === ASYNC_STATUS.SUCCESS) {
      return (
        <div className={styles.greenCheck}>
          <ImageWithFallBack src={greenCheckIconImage} />
        </div>
      )
    }

    return <></>
  }

  return (
    <div className={styles.sparePartsFinderInput}>
      <Row gutter={[100, 0]}>
        <Col lg={10} xs={24}>
          <span className={styles.label}>
            {t('service.selectMachine.itemNumber.label')}
          </span>
          <div className={styles.wrapper}>
            <Input
              autoComplete="off"
              className="custom-input"
              placeholder={t('service.selectMachine.itemNumber.placeHolder')}
              value={itemNumber}
              onChange={handleChange}
            />
            {renderValidationIcon()}
            <Button className={styles.infoButton} onClick={toggleInfoShow}>
              <ImageWithFallBack src={moreInfoIconImage} />
            </Button>
          </div>
          {debouncedItemNumber !== '' && debouncedItemNumber === itemNumber && (
            <SparePartsProductInfo
              key={debouncedItemNumber}
              handleSetIconState={handleSetIconState}
              materialNumber={debouncedItemNumber}
            />
          )}
          <DisisHomeButton />
        </Col>
        <Col lg={14} xs={24}>
          <HowToFindItemNumber
            open={isItemNumberInfoShown}
            onToggle={toggleInfoShow}
          />
        </Col>
      </Row>
    </div>
  )
}

export default SparePartsFinderInput
